import React, { useState, memo, useMemo } from 'react';
import { GraduationCap, BarChart3, BrainCircuit, Baby } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SaisieNotesPrimaire from '../components/SaisieNotesPrimaire';
import SaisieNotes from '../components/SaisieNotes';
import StatistiquesGlobales from '../components/StatistiquesGlobales';
import OutilDecisionnel from '../components/OutilDecisionnel';
import { useFeatures } from '../contexts/FeaturesContext';

/**
 * Carnet d'évaluations.
 *
 * Les onglets sont décrits par un tableau plutôt qu'écrits un à un : c'est ce
 * qui permet d'en retirer un sans laisser de trou. La `TabsList` était en
 * `grid-cols-4` en dur — masquer un onglet aurait laissé une colonne vide.
 *
 * L'outil décisionnel est vendu à part (`module.evaluations.outil_decisionnel`)
 * et n'est pas inclus dans l'offre Essentiel. Les trois autres onglets font
 * partie du Carnet lui-même.
 *
 * ⚠️ L'onglet « Saisies des enseignants » a été retiré d'ici (14/10/2026) : la
 * validation des notes saisies par les enseignants se fait dans le suivi des
 * enseignants, sur la page de chacun. Le carnet n'a pas à en porter une
 * seconde entrée. Aucun onglet ne vit plus dans l'adresse : ils gardent tous
 * leur état local, et cette page n'écrit rien dans l'URL.
 */

const COLONNES = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5'
};

const NotesPage = () => {
  const { estActive } = useFeatures();

  const onglets = useMemo(() => [
    {
      value: 'primaire',
      libelle: 'Primaire',
      icone: Baby,
      actif: 'data-[state=active]:bg-[#319190]',
      contenu: <SaisieNotesPrimaire />
    },
    {
      value: 'college-lycee',
      libelle: 'Collège & Lycée',
      icone: GraduationCap,
      actif: 'data-[state=active]:bg-[#246990]',
      contenu: <SaisieNotes />
    },
    {
      value: 'statistiques',
      libelle: 'Statistiques',
      icone: BarChart3,
      actif: 'data-[state=active]:bg-[#ae7467]',
      contenu: <StatistiquesGlobales />
    },
    {
      value: 'outil-decisionnel',
      libelle: 'Outil décisionnel',
      icone: BrainCircuit,
      actif: 'data-[state=active]:bg-[#7c3aed]',
      contenu: <OutilDecisionnel />,
      cle: 'module.evaluations.outil_decisionnel'
    }
  ].filter((o) => !o.cle || estActive(o.cle)), [estActive]);

  const [selectedMainTab, setSelectedMainTab] = useState('primaire');
  // Un onglet qui disparaît pendant qu'on y est — une licence relue, et l'outil
  // décisionnel s'en va — laisserait la page sans contenu. On retombe sur le
  // premier.
  const ongletAffiche = onglets.some((o) => o.value === selectedMainTab) ? selectedMainTab : 'primaire';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold mb-6 text-[#246990]">Carnet d'évaluations</h1>

        <Tabs
          defaultValue="primaire"
          value={ongletAffiche}
          onValueChange={setSelectedMainTab}
          className="w-full"
        >
          <TabsList className={`${COLONNES[onglets.length] || 'grid-cols-4'} grid mb-8 bg-[#246990]/10 w-full`}>
            {onglets.map((o) => {
              const Icone = o.icone;
              return (
                <TabsTrigger
                  key={o.value}
                  value={o.value}
                  className={`flex items-center justify-center gap-2 ${o.actif} data-[state=active]:text-white min-w-0`}
                >
                  <Icone size={16} />
                  <span className="hidden md:inline whitespace-nowrap">{o.libelle}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {onglets.map((o) => (
            <TabsContent key={o.value} value={o.value} className="w-full">
              {o.contenu}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default memo(NotesPage);
